import React from "react";
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  FormControlLabel,
  Grid,
  Input,
  InputLabel,
  TextareaAutosize,
  TextField,
  Typography,
  DialogContent,
  IconButton,
} from "@mui/material";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { ErrorMessage, Form, Formik } from "formik";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TagsInput } from "react-tag-input-component";
import { useAddPartMutation } from "../../../redux/reducer/features/Part/partSlice";
import LenghtUnitList from "../../../compenents/app/LenghtUnitList";
import MassUnitList from "../../../compenents/app/MassUnitList";
import PageLoader from "../../../compenents/app/PageLoader";
import { useDispatch } from "react-redux";
import {
  Part,
  PartInitialValues,
  PartValidationSchema,
  QuickPartValidationSchema,
} from "../../../models/features/Part/Parts";
import { useGetManufacturersQuery } from "../../../redux/reducer/features/Product/productSlice";
import ManagePartImage from "../ManagePartImage";
import AppGrid from "../../app/AppGrid";
import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";

export interface AddDialogProps {
  isOpen: boolean;
  onClose: any;
}

export const AddDialog: React.FunctionComponent<AddDialogProps> = (props) => {
  const dispatch = useDispatch();
  const [partMutation, partMutationResult] = useAddPartMutation();
  const manufacturers = useGetManufacturersQuery();
  const [partData, setPartData] = React.useState<Part>();
  const [partList, setpartList] = React.useState<Part[]>([]);

  const manufacturerDefaultProps = {
    options:
      manufacturers.data && manufacturers.data.entityList
        ? manufacturers.data.entityList
        : [],
    getOptionLabel: (option: any) => option.title,
  };

  const handleOnSubmit = (values: Part) => {
    partMutation({
      data: {
        ...values,
        dimension: {
          depth: 0,
          depthUnit: 0,
          height: 0,
          heightUnit: 0,
          weight: 0,
          weightUnit: 0,
          width: 0,
          widthUnit: 0,
        },
        description: "",
        tags: [],
        isEnabled: true,
      },
    });
  };

  React.useEffect(() => {
    if (partMutationResult.isSuccess && partMutationResult.data.entity) {
      setpartList([...partList, partMutationResult.data.entity]);
    }
  }, [partMutationResult]);

  const columns: GridColDef[] = [
    { field: "partNumber", headerName: "Part Number", width: 220 },
    {
      field: "title",
      headerName: "Title",
      width: 220,
    },
    {
      field: "shortName",
      headerName: "Short Name",
      width: 200,
    },
    {
      field: "manufacturer",
      headerName: "Manufacturer",
      width: 200,
      editable: true,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.manufacturer.title,
    },

    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,

      renderCell: (params: GridRenderCellParams) => {
        return (
          <div>
            <MoreVertIcon />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageLoader isLoading={false} />
      <Dialog
        open={props.isOpen}
        onClose={() => {
          props.onClose();
        }}
        maxWidth={"lg"}
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 0,
            paddingLeft: "39px",
            paddingRight: "39px",
          }}
        >
          <h4>Add Part</h4>
          <IconButton
            onClick={() => {
              props.onClose();
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: 0 }}>
          <Container maxWidth="xl">
            <Formik
              initialValues={partData ?? PartInitialValues}
              validationSchema={QuickPartValidationSchema}
              onSubmit={(values) => {
                handleOnSubmit(values);
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setFieldValue,
              }) => (
                <>
                  <Form>
                    <Grid container spacing={3} p={2} pt={0}>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          id="partNumber"
                          name="partNumber"
                          label="Part Number"
                          fullWidth
                          autoComplete="given-name"
                          variant="standard"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.partNumber}
                        />
                        <ErrorMessage
                          className="error-msg"
                          component={"div"}
                          name="partNumber"
                        />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <TextField
                          id="title"
                          name="title"
                          label="Title"
                          fullWidth
                          autoComplete="family-name"
                          variant="standard"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.title}
                        />
                        <ErrorMessage
                          className="error-msg"
                          component={"div"}
                          name="title"
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          id="shortName"
                          name="shortName"
                          label="Short Name"
                          fullWidth
                          autoComplete="family-name"
                          variant="standard"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.shortName}
                        />
                        <ErrorMessage
                          className="error-msg"
                          component={"div"}
                          name="shortName"
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Autocomplete
                          {...manufacturerDefaultProps}
                          id="clear-on-escape"
                          clearOnEscape
                          defaultValue={partData?.manufacturer}
                          onChange={(e, value) => {
                            console.log(values);
                            setFieldValue(
                              "manufacturerId",
                              value ? value.id : ""
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Manufacturer"
                              variant="standard"
                            />
                          )}
                        />
                        <ErrorMessage
                          className="error-msg"
                          component={"div"}
                          name="manufacturerId"
                        />
                      </Grid>
                      <Grid item xs={12} textAlign={"right"}>
                        <Button
                          variant="contained"
                          type="submit"
                          onClick={() => {
                            console.log(errors);
                            console.log(values);
                          }}
                          endIcon={
                            partMutationResult.isLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : (
                              <></>
                            )
                          }
                          disabled={partMutationResult.isLoading}
                          size="small"
                        >
                          Add & Save
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                </>
              )}
            </Formik>
            <AppGrid
              columns={columns}
              data={partList}
              isLoading={false}
              height={500}
            ></AppGrid>
          </Container>
        </DialogContent>
      </Dialog>
    </>
  );
};
